import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MdbModalRef } from 'mdb-angular-ui-kit/modal';
import { SavedCalculatorService, AlertService } from '../../../core/services';
import { CalculatorType, CreateSavedCalculatorDto, CalculatorData } from '../../../core/models';
import { MdbFormsModule } from "mdb-angular-ui-kit/forms";

export interface SaveCalculatorModalData {
  calculatorType: CalculatorType;
  calculatorData: CalculatorData;
}

@Component({
  selector: 'app-save-calculator-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MdbFormsModule],
  templateUrl: './save-calculator-modal.component.html'
})
export class SaveCalculatorModalComponent implements OnInit {
  modalRef = inject(MdbModalRef<SaveCalculatorModalComponent>);
  private savedCalculatorService = inject(SavedCalculatorService);
  private alertService = inject(AlertService);
  private fb = inject(FormBuilder);

  // Dati passati alla modale
  calculatorType?: CalculatorType;
  calculatorData?: CalculatorData;

  // Reactive Form
  saveForm!: FormGroup;

  // Stati
  isSaving: boolean = false;
  error: string | null = null;

  ngOnInit(): void {
    // Inizializza il form reattivo
    this.saveForm = this.fb.group({
      title: ['', [Validators.required, Validators.maxLength(100)]],
      description: ['', [Validators.maxLength(500)]]
    });

    // I dati vengono passati tramite la configurazione della modale
  }

  /**
   * Getter per accedere facilmente ai controlli del form
   */
  get titleControl() {
    return this.saveForm.get('title');
  }

  get descriptionControl() {
    return this.saveForm.get('description');
  }

  /**
   * Salva il calcolatore
   */
  save(): void {
    // Marca tutti i campi come touched per mostrare gli errori
    if (this.saveForm.invalid) {
      this.saveForm.markAllAsTouched();
      if (this.titleControl?.hasError('required')) {
        this.error = 'Il titolo è obbligatorio';
      }
      return;
    }

    if (!this.calculatorType || !this.calculatorData) {
      this.error = 'Dati del calcolatore mancanti';
      return;
    }

    this.isSaving = true;
    this.error = null;

    const formValue = this.saveForm.value;
    const createDto: CreateSavedCalculatorDto = {
      type: this.calculatorType,
      title: formValue.title.trim(),
      description: formValue.description?.trim() || undefined,
      inputs: this.calculatorData
    };

    this.savedCalculatorService.createCalculator(createDto).subscribe({
      next: (savedCalculator) => {
        this.isSaving = false;
        // Mostra alert di successo
        this.alertService.success('Calcolatore salvato con successo!');
        // Chiudi la modale e passa i dati salvati
        this.modalRef.close(savedCalculator);
      },
      error: (error) => {
        console.error('Errore nel salvataggio del calcolatore:', error);
        this.isSaving = false;
        const errorMessage = error?.error?.message || 'Errore nel salvataggio del calcolatore. Riprova più tardi.';
        this.error = errorMessage;
        // Mostra alert di errore
        this.alertService.error(errorMessage);
      }
    });
  }

  /**
   * Chiude la modale senza salvare
   */
  cancel(): void {
    this.modalRef.close(null);
  }

  /**
   * Resetta l'errore quando l'utente modifica i campi
   */
  clearError(): void {
    this.error = null;
  }
}
